import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import { Loader2 } from 'lucide-react';
import LandingPage from '../LandingPage';
import { AuthView } from '../components/AuthView';
import { PublicView } from '../components/PublicView';
// EditorView removed — now lazy-loaded directly in App.jsx and reads state from AppContext.
import { auth, db } from '../firebaseInstance';
import { getAppId } from '../firebaseConfig';

const appId = getAppId();

// --- PROTECTED ROUTE COMPONENT ---
export const ProtectedRoute = ({ children, user, isLoaded }) => {
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader2 size={32} className="animate-spin text-[#C1FF00]" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// --- ROUTE WRAPPER COMPONENTS ---
export const LandingRoute = ({ user }) => {
  const navigate = useNavigate();
  
  // If user is authenticated, redirect to editor
  useEffect(() => {
    if (user) {
      navigate('/editor', { replace: true });
    }
  }, [user, navigate]);
  
  // If not authenticated, show landing page
  if (user) {
    return null; // Will redirect
  }
  
  return (
    <LandingPage
      onGetStarted={(usernameOrEvent) => {
        const username = typeof usernameOrEvent === 'string' ? usernameOrEvent : undefined;
        const params = new URLSearchParams({ mode: 'signup' });
        if (username && username.trim()) params.set('username', username.trim());
        navigate(`/auth?${params.toString()}`);
      }}
    />
  );
};

export const AuthRoute = ({ user, onAuthSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user) {
      const searchParams = new URLSearchParams(location.search);
      const username = searchParams.get('username');
      const editorUrl = username ? `/editor?username=${encodeURIComponent(username)}` : '/editor';
      navigate(editorUrl, { replace: true });
    }
  }, [user, navigate, location.search]);
  
  return <AuthView onAuthSuccess={onAuthSuccess} />;
};

export const PreviewRoute = ({ profileData, user }) => {
  const navigate = useNavigate();
  
  const handleBack = () => {
    navigate('/editor');
  };
  
  return <PublicView profileData={profileData} onBack={handleBack} profileOwnerId={user?.uid} user={user} />;
};

export const PublicProfileRoute = () => {
  const { username } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [profileOwnerId, setProfileOwnerId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get current user for subscription functionality
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);
  
  // Clean tracking parameters from URL on mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const trackingParams = ['fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'ref', 'igshid'];
    let hasTrackingParams = false;
    
    trackingParams.forEach(param => {
      if (searchParams.has(param)) {
        searchParams.delete(param);
        hasTrackingParams = true;
      }
    });
    
    // If we removed tracking params, update URL without them
    if (hasTrackingParams) {
      const newSearch = searchParams.toString();
      const newUrl = newSearch 
        ? `${location.pathname}?${newSearch}` 
        : location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [location, navigate]);
  
  // Reserved routes that should not be treated as usernames
  const reservedRoutes = [
    'auth', 'editor', 'preview', 'profile',
    'login', 'dashboard', 'settings', 'account', 'signup', 'register',
    'blog', 'privacy', 'terms', 'comparison', 'solutions', 'templates', 'profiles',
    'admin', 'api', 'static', 'assets'
  ];
  
  useEffect(() => {
    // If it's a reserved route, redirect to home
    if (reservedRoutes.includes(username?.toLowerCase())) {
      navigate('/', { replace: true });
      return;
    }
    
    // Fetch profile by username from Firestore
    const fetchProfileByUsername = async () => {
      try {
        // Query Firestore for user with matching username (case-sensitive)
        const usersRef = collection(db, 'artifacts', appId, 'users');
        const q = query(usersRef, where('username', '==', username), limit(1));
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const data = doc.data();
          
          // Ensure contactInfo structure exists with proper defaults
          const profileDataWithDefaults = {
            ...data,
            contactInfo: {
              phones: (data.contactInfo?.phones || []).filter(p => p && p.number && p.number.trim()),
              emails: (data.contactInfo?.emails || []).filter(e => e && e.email && e.email.trim()),
              ...(data.contactInfo || {})
            },
            // Ensure showcases array exists
            showcases: data.showcases || []
          };
          
          setProfileData(profileDataWithDefaults);
          setProfileOwnerId(doc.id); // Store profile owner's user ID
        } else {
          // Username not found - log for debugging
          console.log(`Profile not found for username: "${username}"`);
          setProfileData(null);
          setProfileOwnerId(null);
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        console.error('Username searched:', username);
        console.error('App ID:', appId);
        setProfileData(null);
        setProfileOwnerId(null);
      } finally {
        setLoading(false);
      }
    };
    
    if (username) {
      fetchProfileByUsername();
    } else {
      setLoading(false);
    }
  }, [username, navigate]);
  
  // Update meta tags for social media previews when profile loads
  useEffect(() => {
    if (profileData) {
      // CRITICAL: Clear any existing meta tags first to prevent stale data
      // This ensures we don't show cached "Sarah Jenkins" or other stale content
      const clearMetaTags = () => {
        const metaTagsToClear = [
          'og:title', 'og:description', 'og:image', 'og:image:secure_url', 
          'og:image:width', 'og:image:height', 'og:image:type',
          'twitter:card', 'twitter:title', 'twitter:description', 'twitter:image',
          'description'
        ];
        metaTagsToClear.forEach(prop => {
          const meta = document.querySelector(`meta[property="${prop}"]`) || 
                       document.querySelector(`meta[name="${prop}"]`);
          if (meta) {
            meta.remove();
          }
        });
      };
      
      // Clear old meta tags first
      clearMetaTags();
      
      // Helper function to update or create meta tags
      const updateMetaTag = (property, content) => {
        let meta = document.querySelector(`meta[property="${property}"]`) || 
                   document.querySelector(`meta[name="${property}"]`);
        
        if (!meta) {
          meta = document.createElement('meta');
          if (property.startsWith('og:') || property.startsWith('article:')) {
            meta.setAttribute('property', property);
          } else {
            meta.setAttribute('name', property);
          }
          document.head.appendChild(meta);
        }
        meta.setAttribute('content', content);
      };
      
      // CRITICAL: Only update meta tags if we have valid profile data with a name
      // This prevents showing "Profile" or default content
      if (!profileData.name || !profileData.name.trim()) {
        return; // Don't update meta tags if no valid name
      }
      
      // Title format: {Name}: {Bio/Tagline}: PerAI.me (max 60 chars for SEO)
      // SEO best practice: Keep titles 50-60 characters to avoid truncation in search results
      const name = profileData.name.trim();
      let title = '';
      
      if (profileData.tagline && profileData.tagline.trim()) {
        // Use tagline if available (usually shorter and more descriptive)
        const tagline = profileData.tagline.trim();
        const remainingChars = 60 - name.length - 12; // Reserve space for ": " + ": PerAI.me"
        if (tagline.length <= remainingChars) {
          title = `${name}: ${tagline}: PerAI.me`;
        } else {
          title = `${name}: ${tagline.substring(0, remainingChars - 3)}...: PerAI.me`;
        }
      } else if (profileData.bio && profileData.bio.trim()) {
        // Fallback to bio, but truncate to fit in 60 chars
        const remainingChars = 60 - name.length - 12; // Reserve space for ": " + ": PerAI.me"
        const bioText = profileData.bio.trim();
        if (bioText.length <= remainingChars) {
          title = `${name}: ${bioText}: PerAI.me`;
        } else {
          title = `${name}: ${bioText.substring(0, remainingChars - 3)}...: PerAI.me`;
        }
      } else {
        title = `${name}: PerAI.me`;
      }
      
      // Update document title
      document.title = title;
      
      // Description format: {Tagline} - Chat with {Name}'s AI on PerAI
      let description = '';
      if (profileData.tagline && profileData.tagline.trim()) {
        description = `${profileData.tagline} - Chat with ${name}'s AI on PerAI`;
      } else if (profileData.bio && profileData.bio.trim()) {
        const bioText = profileData.bio.length > 120 
          ? profileData.bio.substring(0, 120) + '...' 
          : profileData.bio;
        description = `${bioText} - Chat with ${name}'s AI on PerAI`;
      } else {
        description = `Chat with ${name}'s AI on PerAI`;
      }
      
      const profileUrl = `${window.location.origin}/${profileData.username}`;
      // CRITICAL: Don't use default image - only use if valid avatar exists
      // This prevents showing default image before actual profile loads
      const image = (profileData.avatar && 
                    profileData.avatar.trim() && 
                    !profileData.avatar.startsWith('data:') &&
                    (profileData.avatar.startsWith('http://') || profileData.avatar.startsWith('https://')))
                    ? profileData.avatar.trim()
                    : null;
      
      // Open Graph tags
      updateMetaTag('og:title', title);
      updateMetaTag('og:description', description);
      // Only include image tags if we have a valid avatar
      if (image) {
        updateMetaTag('og:image', image);
        updateMetaTag('og:image:secure_url', image);
        updateMetaTag('og:image:alt', `${name} on PerAI.me`);
      }
      updateMetaTag('og:url', profileUrl);
      updateMetaTag('og:type', 'profile');
      updateMetaTag('og:site_name', 'PerAI.me');
      
      // Twitter Card tags
      updateMetaTag('twitter:card', image ? 'summary_large_image' : 'summary');
      updateMetaTag('twitter:title', title);
      updateMetaTag('twitter:description', description);
      if (image) {
        updateMetaTag('twitter:image', image);
      }
      
      // Standard meta tags
      updateMetaTag('description', description);

      // Person structured data
      const personSchema = {
        "@context": "https://schema.org",
        "@type": "Person",
        "name": name,
        "description": description,
        "url": profileUrl,
        ...(image && { "image": image }),
        ...(profileData.username && { "identifier": profileData.username }),
      };
      const existingPersonSchema = document.querySelector('script[type="application/ld+json"][data-person-schema]');
      if (existingPersonSchema) existingPersonSchema.remove();
      const personSchemaScript = document.createElement('script');
      personSchemaScript.type = 'application/ld+json';
      personSchemaScript.setAttribute('data-person-schema', 'true');
      personSchemaScript.textContent = JSON.stringify(personSchema);
      document.head.appendChild(personSchemaScript);
    }

    // Cleanup: Reset to default when component unmounts
    return () => {
      document.title = 'PerAI.me | The Bio That Speaks';
      const personSchemaToRemove = document.querySelector('script[type="application/ld+json"][data-person-schema]');
      if (personSchemaToRemove) personSchemaToRemove.remove();
    };
  }, [profileData]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <Loader2 size={32} className="animate-spin text-[#C1FF00]" />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center px-4">
          <h1 className="text-3xl font-black text-white uppercase tracking-tight mb-3 font-['Plus_Jakarta_Sans',sans-serif]">Profile Not Found</h1>
          <p className="text-gray-400 mb-6 font-['Courier_New',monospace] text-sm uppercase tracking-widest">This handle doesn't exist yet.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#C1FF00] text-black font-black text-sm uppercase tracking-widest rounded-xl hover:bg-[#C1FF00]/90 transition-all shadow-[0_4px_0_0_#C1FF00] hover:shadow-[0_2px_0_0_#C1FF00] active:translate-y-[2px] active:shadow-none font-['Courier_New',monospace]"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }
  
  return <PublicView profileData={profileData} onBack={null} profileOwnerId={profileOwnerId} user={user} />;
};

